import './index.css'
import debounce from 'lodash/debounce'

import React, { useCallback, useRef } from 'react'
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer'
import InfiniteLoader from 'react-virtualized/dist/commonjs/InfiniteLoader'
import List from 'react-virtualized/dist/commonjs/List'
import WindowScroller from 'react-virtualized/dist/commonjs/WindowScroller'
import type { Contact, RecipientList } from 'types/list'
import Skeleton from 'components/ui/skeleton'
import ContactsMenuAddButtons from 'pages/contacts/add/buttons'
import ContactHeader from '../contact-header'
import ContactRow from '../contact-row'
import ContactsMessage from '../contacts-message'
import DefaultRow from '../default-row'
type Props = {
  // TODO: Fix type
  headers?: any
  columns: any
  sortContacts?: (...args: Array<any>) => any
  onRowModeSwitch?: (...args: Array<any>) => any
  onSelect?: (...args: Array<any>) => any
  onFieldChange?: (...args: Array<any>) => any
  onFieldValueAdd?: (...args: Array<any>) => any
  onFieldValueRemove?: (...args: Array<any>) => any
  onSelectAll?: (...args: Array<any>) => any
  sortParam?: string
  sortDirection?: string
  searchTerm?: string
  emptyText?: React.ReactNode
  beingSearched?: boolean
  appendingContacts?: boolean
  list?: RecipientList | null | undefined
  truncationLimit?: number
  contacts: Contact[]
  rowRenderer?: string | null | undefined
  loading?: boolean
  loaded?: boolean
  error?: string | null | undefined
  loadContacts?: (...args: Array<any>) => any
  contactsCount?: number
}

const ContactsTable = ({
  headers,
  columns,
  sortContacts,
  onRowModeSwitch,
  onSelect,
  onFieldChange,
  onFieldValueAdd,
  onFieldValueRemove,
  onSelectAll,
  sortParam,
  sortDirection,
  searchTerm,
  emptyText,
  beingSearched,
  appendingContacts,
  list,
  truncationLimit,
  contacts,
  rowRenderer,
  loading,
  loaded,
  error,
  loadContacts,
  contactsCount,
}: Props): React.ReactNode | React.ReactElement<'div'> => {
  const contactsToLoad = useRef<{
    startIndex: number | null
    stopIndex: number | null
  }>({
    startIndex: null,
    stopIndex: null,
  })
  const selectedAll = loaded
    ? !contacts.filter(c => c.loaded).some(c => !c.selected)
    : false
  const showTable = (loaded || rowRenderer === 'default') && !!contacts.length
  const handleLoadContacts = useCallback(
    (startIndex: number, stopIndex: number) => {
      if (loadContacts) {
        loadContacts(startIndex, stopIndex + 1)
        contactsToLoad.current = {
          startIndex: null,
          stopIndex: null,
        }
      }
    },
    [loadContacts],
  )
  const debouncedLoadContacts = useCallback(
    debounce((startIndex: number, stopIndex: number) => {
      handleLoadContacts(startIndex, stopIndex)
    }, 200),
    [handleLoadContacts],
  )

  const loadMoreRows = ({
    startIndex,
    stopIndex,
  }: {
    startIndex: number
    stopIndex: number
  }) => {
    const { startIndex: prevStartIndex, stopIndex: prevStopIndex } =
      contactsToLoad.current
    let newStartIndex = startIndex

    if (typeof prevStartIndex === 'number' && prevStartIndex < startIndex) {
      newStartIndex = prevStartIndex
    }

    let newStopIndex = stopIndex

    if (typeof prevStopIndex === 'number') {
      if (stopIndex > prevStopIndex) {
        newStopIndex = stopIndex
      } else {
        newStopIndex = prevStopIndex
      }
    }

    contactsToLoad.current = {
      startIndex: newStartIndex,
      stopIndex: newStopIndex,
    }

    if (typeof newStartIndex === 'number' && typeof newStopIndex === 'number') {
      if (newStopIndex - newStartIndex > 100) {
        handleLoadContacts(newStartIndex, newStopIndex)
      } else {
        debouncedLoadContacts(newStartIndex, newStopIndex)
      }
    }
  }

  const renderRow = ({
    index,
    style,
  }: {
    index: number
    style?: Record<string, any>
  }) => {
    const contact = contacts[index]
    if (!contact) return
    if (!contact?.loaded && !(rowRenderer === 'default'))
      return (
        <div key={`contact-${index}`} style={style}>
          <div className="ContactRow">
            <div className="ContactCell">
              <Skeleton />
            </div>
          </div>
        </div>
      )
    return rowRenderer === 'default' ? (
      <div key={`contact-${contact.id}`} style={style}>
        <DefaultRow
          columns={columns}
          contact={contact}
          list={list}
          onRowModeSwitch={onRowModeSwitch}
          appendingContacts={appendingContacts}
          onFieldChange={onFieldChange}
          onFieldValueAdd={onFieldValueAdd}
          onFieldValueRemove={onFieldValueRemove}
        />
      </div>
    ) : (
      <div key={`contact-${contact.id}`} style={style}>
        <ContactRow
          columns={columns}
          contactId={contact.id}
          list={list}
          onRowModeSwitch={onRowModeSwitch}
          appendingContacts={appendingContacts}
          onFieldChange={onFieldChange}
          onFieldValueAdd={onFieldValueAdd}
          onFieldValueRemove={onFieldValueRemove}
          onSelect={onSelect}
        />
      </div>
    )
  }

  const isRowLoaded = ({ index }: { index: number }) => {
    return contacts[index]?.loaded
  }

  const renderTable = () => {
    const batchMode = contacts.filter(c => c.editMode).length > 1

    if (truncationLimit) {
      let note = null

      if (contacts.length >= truncationLimit) {
        const more = contacts.length - truncationLimit
        note = (
          <div className="ContactRow">
            <div className="ContactsTable_Truncated">
              and {more} more{' '}
              <span>{more === 1 ? 'contact' : 'contacts'}</span>
            </div>
          </div>
        )
      }

      return (
        <div>
          {contacts.slice(0, truncationLimit).map((_, index) =>
            renderRow({
              index,
            }),
          )}
          {note}
        </div>
      )
    } else if (rowRenderer === 'default') {
      return (
        <WindowScroller>
          {({ height, isScrolling, onChildScroll, scrollTop }) => (
            <AutoSizer disableHeight>
              {({ width }) => (
                <List
                  autoHeight
                  width={width}
                  height={height}
                  isScrolling={isScrolling}
                  onScroll={onChildScroll}
                  scrollTop={scrollTop}
                  rowCount={contacts.length}
                  rowHeight={40}
                  rowRenderer={renderRow}
                  overscanRowCount={30} // pass through so react-virtualized can know when to rerender row
                  searchTerm={searchTerm}
                  sortParam={sortParam}
                  sortDirection={sortDirection}
                  batchMode={batchMode}
                />
              )}
            </AutoSizer>
          )}
        </WindowScroller>
      )
    } else {
      return (
        <InfiniteLoader
          isRowLoaded={isRowLoaded}
          loadMoreRows={loadMoreRows}
          rowCount={contactsCount}
          minimumBatchSize={1}
          threshold={0}
        >
          {({ onRowsRendered, registerChild }) => (
            <WindowScroller>
              {({ height, isScrolling, onChildScroll, scrollTop }) => (
                <AutoSizer disableHeight>
                  {({ width }) => (
                    <List
                      ref={registerChild}
                      autoHeight
                      width={width}
                      height={height}
                      onRowsRendered={onRowsRendered}
                      isScrolling={isScrolling}
                      onScroll={onChildScroll}
                      rowCount={contactsCount}
                      rowHeight={40}
                      rowRenderer={({ index, style }) =>
                        renderRow({
                          index,
                          style,
                        })
                      }
                      scrollTop={scrollTop}
                      overscanRowCount={30} // pass through so react-virtualized can know when to rerender row
                      searchTerm={searchTerm}
                      sortParam={sortParam}
                      sortDirection={sortDirection}
                      batchMode={batchMode}
                    />
                  )}
                </AutoSizer>
              )}
            </WindowScroller>
          )}
        </InfiniteLoader>
      )
    }
  }

  if (error) {
    return (
      <div className="ContactsTable_Empty">
        <ContactsMessage iconType="error" header="Failed to load contacts" />
      </div>
    )
  }

  if (
    (searchTerm || beingSearched) &&
    contacts.length === 0 &&
    !loading &&
    loaded
  ) {
    return (
      <div className="ContactsTable_Empty">
        {emptyText || (
          <ContactsMessage
            iconType="search"
            header={emptyText || 'Sorry! We couldn’t find any contacts'}
            message="Try entering another search term"
          />
        )}
      </div>
    )
  }

  if (contacts.length === 0 && appendingContacts && !loading && loaded) {
    return (
      <div className="ContactsTable_Empty">
        <ContactsMessage
          iconType="search"
          header="Good news! All your contacts are in this list 👍"
        />
      </div>
    )
  }

  if (contacts.length === 0 && !loading && loaded) {
    return (
      <div className="ContactsTable_Empty">
        <ContactsMessage iconType="people" header="No contacts yet">
          <ContactsMenuAddButtons list={list} />
        </ContactsMessage>
      </div>
    )
  }

  return (
    <>
      <ContactHeader
        columns={headers || columns}
        sortContacts={sortContacts}
        sortParam={sortParam}
        sortDirection={sortDirection}
        selectedAll={selectedAll}
        onSelectAll={onSelectAll}
        disabled={loading}
      />
      <div className="ContactsTable">{showTable && renderTable()}</div>
      {!showTable && <div className="ContactsTable-Loader">Loading...</div>}
    </>
  )
}

export default ContactsTable
